import AccountDetailsSection from 'v2/layouts/AccountWorkspaceLayout/components/AccountDetailsSection';
import CertificationsSection from 'v2/layouts/AccountWorkspaceLayout/components/CertificationsSection';
import type { NavigationItem } from 'v2/layouts/NavigationLayout/types';

import AccountIcon from 'components/icons/AccountIcon';
import DetailsIcon from 'components/icons/DetailsIcon';

import {
    ACCOUNT_CERTIFICATIONS,
    ACCOUNT_NEW_APPLICATOR_CERTIFICATION,
    ACCOUNT_PAGE,
    EDIT_APPLICATOR_CERTIFICATION_PAGE,
    EDIT_USER_DETAILS_PAGE
} from 'hooks/usePageRoutes/constants';

import english from './translations/english';
import type {
    AccountWorkspaceLocation,
    AccountWorkspaceSection,
    AccountWorkspaceTab
} from './types';

export {
    ACCOUNT_EDIT_ROUTE,
    ACCOUNT_NEW_ROUTE,
    EDIT_PANEL_ROUTE_PREFIXES,
    getIsEditPanelOpen
} from './routing';

export const TEST_ID = 'accountWorkspaceLayout';

export const ACCOUNT_SECTION = 'account';

export const CERTIFICATIONS_SECTION = 'certifications';

export const CERTIFICATIONS_TAB = 'certifications';

export const CERTIFICATIONS_ROUTE = ACCOUNT_CERTIFICATIONS;

export const NEW_APPLICATOR_CERTIFICATION_ROUTE = ACCOUNT_NEW_APPLICATOR_CERTIFICATION;

export const EDIT_APPLICATOR_CERTIFICATION_ROUTE = EDIT_APPLICATOR_CERTIFICATION_PAGE;

export const DETAILS_TAB = 'details';

export const FIRST_TAB_INDEX = 0;

export const NOT_FOUND_TAB_INDEX = -1;

export const INITIAL_SECTION_CONTENT_KEY = 0;

export const SECTION_CONTENT_KEY_INCREMENT = 1;

export const MY_ACCOUNT_ROUTE = `${ACCOUNT_PAGE}/myaccount`;

export const SECTION_TABS: Record<AccountWorkspaceSection, AccountWorkspaceTab[]> = {
    [ACCOUNT_SECTION]: [
        { content: AccountDetailsSection, id: DETAILS_TAB, route: MY_ACCOUNT_ROUTE }
    ],
    [CERTIFICATIONS_SECTION]: [
        {
            content: CertificationsSection,
            id: CERTIFICATIONS_TAB,
            refreshOnPanelClose: false,
            route: CERTIFICATIONS_ROUTE
        }
    ]
};

export const DEFAULT_WORKSPACE_LOCATION: AccountWorkspaceLocation = {
    section: ACCOUNT_SECTION,
    tabId: DETAILS_TAB
};

export const WORKSPACE_LOCATIONS: {
    location: AccountWorkspaceLocation;
    pathPrefix: string;
}[] = [
    {
        location: { section: CERTIFICATIONS_SECTION, tabId: CERTIFICATIONS_TAB },
        pathPrefix: EDIT_APPLICATOR_CERTIFICATION_ROUTE
    },
    {
        location: { section: CERTIFICATIONS_SECTION, tabId: CERTIFICATIONS_TAB },
        pathPrefix: NEW_APPLICATOR_CERTIFICATION_ROUTE
    },
    {
        location: { section: CERTIFICATIONS_SECTION, tabId: CERTIFICATIONS_TAB },
        pathPrefix: CERTIFICATIONS_ROUTE
    }
];

export const ACCOUNT_NAVIGATION_ITEM: NavigationItem = {
    activePathPrefixes: [MY_ACCOUNT_ROUTE, EDIT_USER_DETAILS_PAGE],
    href: MY_ACCOUNT_ROUTE,
    icon: AccountIcon,
    id: ACCOUNT_SECTION,
    label: english.account
};

export const CERTIFICATIONS_NAVIGATION_ITEM: NavigationItem = {
    activePathPrefixes: [
        CERTIFICATIONS_ROUTE,
        NEW_APPLICATOR_CERTIFICATION_ROUTE,
        EDIT_APPLICATOR_CERTIFICATION_ROUTE
    ],
    href: CERTIFICATIONS_ROUTE,
    icon: DetailsIcon,
    id: CERTIFICATIONS_SECTION,
    label: english.certifications
};

export const getAccountNavigationItems = (isStateReportingEnabled: boolean): NavigationItem[] =>
    isStateReportingEnabled
        ? [ACCOUNT_NAVIGATION_ITEM, CERTIFICATIONS_NAVIGATION_ITEM]
        : [ACCOUNT_NAVIGATION_ITEM];

export const getWorkspaceLocation = (pathname: string): AccountWorkspaceLocation =>
    WORKSPACE_LOCATIONS.find(workspaceLocation => pathname.startsWith(workspaceLocation.pathPrefix))
        ?.location ?? DEFAULT_WORKSPACE_LOCATION;
